import React, { useEffect } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  isOpen?: boolean;
  open?: boolean; // Ant Design compatibility alias
  onClose?: () => void;
  onCancel?: () => void; // Ant Design compatibility alias
  title: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  width?: string | number;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  open,
  onClose,
  onCancel,
  title,
  children,
  size = "md",
  showCloseButton = true,
  footer,
  width,
}) => {
  // Support both isOpen and open props (Ant Design compatibility)
  const isVisible = isOpen ?? open ?? false;
  const handleClose = onClose ?? onCancel ?? (() => {});
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isVisible, handleClose]);

  if (!isVisible) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
    full: "w-full h-full max-w-none max-h-none m-0 rounded-none",
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className={size === 'full' ? 'fixed inset-0 z-10' : 'flex min-h-full items-center justify-center p-4'}>
        <div
          className={`relative bg-primary shadow-xl overflow-hidden ${
            size === 'full'
              ? 'w-full h-full border-0 rounded-none'
              : `w-full rounded-lg border border-secondary ${sizeClasses[size]} max-h-[90vh]`
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-tertiary">
            <h3 className="text-xl font-semibold text-primary">{title}</h3>
            {showCloseButton && (
              <button
                onClick={handleClose}
                className="text-tertiary hover:text-primary transition-colors p-1 rounded-md hover:bg-secondary"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Content */}
          <div className={`overflow-y-auto ${size === 'full' ? 'h-[calc(100vh-5rem)]' : 'max-h-[calc(90vh-4rem)]'}`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 p-6 border-t border-tertiary">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
